<template>
  <div class="page-wrap">
    <div class="panel-card">

      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-terminal"></i>
          Cron Job Logs
        </span>
        <button class="btn-outline" @click="refreshAll()">
          <i class="fa fa-sync" :class="{ 'fa-spin': loadingList }"></i> Refresh
        </button>
      </div>

      <div class="layout">
        <!-- Job list -->
        <div class="job-list">
          <div class="state-box" v-if="loadingList">
            <i class="fa fa-spinner fa-spin"></i> Loading…
          </div>
          <div class="state-box" v-else-if="jobs.length === 0">
            No cron jobs with log files found
          </div>
          <div
            v-else
            class="job-row"
            v-for="job in jobs"
            :key="job.name"
            :class="{ active: selectedJob === job.name }"
            @click="selectJob(job.name)"
          >
            <div class="job-name">
              <i class="fa fa-clock"></i>
              {{ job.name }}
            </div>
            <div class="job-meta">
              <span class="job-schedule">{{ job.schedule }}</span>
              <span class="badge-pill" :class="job.exists ? 's-yes' : 's-no'">
                {{ job.exists ? 'Has log' : 'No log yet' }}
              </span>
            </div>
            <div class="job-modified" v-if="job.modified">
              Last run: {{ job.modified }}
            </div>
          </div>
        </div>

        <!-- Log content -->
        <div class="log-panel">
          <div class="state-box" v-if="!selectedJob">
            <i class="fa fa-arrow-left"></i> Select a cron job to view its log
          </div>
          <template v-else>
            <div class="log-toolbar">
              <div class="log-path" :title="logPath">{{ logPath }}</div>
              <div class="log-toolbar-actions">
                <select v-model.number="linesLimit" @change="loadLogContent()">
                  <option :value="200">Last 200 lines</option>
                  <option :value="500">Last 500 lines</option>
                  <option :value="1000">Last 1000 lines</option>
                  <option :value="2000">Last 2000 lines</option>
                </select>
                <button class="btn-outline sm" @click="loadLogContent()">
                  <i class="fa fa-sync" :class="{ 'fa-spin': loadingContent }"></i> Refresh
                </button>
              </div>
            </div>
            <div class="state-box" v-if="loadingContent">
              <i class="fa fa-spinner fa-spin"></i> Loading log…
            </div>
            <div class="state-box" v-else-if="logLines.length === 0">
              No log content yet
            </div>
            <pre class="log-content" v-else ref="logContent">{{ logLines.join('\n') }}</pre>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { FETCH_CRON_LOG_LIST, FETCH_CRON_LOG_CONTENT } from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Cron Job Logs", route: "" }
      ],
      jobs: [],
      loadingList: false,
      selectedJob: null,
      logLines: [],
      logPath: "",
      loadingContent: false,
      linesLimit: 500
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadJobList();
  },
  methods: {
    loadJobList() {
      this.loadingList = true;
      this.$store.dispatch(FETCH_CRON_LOG_LIST)
        .then(data => {
          this.jobs = data || [];
          this.loadingList = false;
        })
        .catch(() => {
          this.loadingList = false;
        });
    },
    refreshAll() {
      this.loadJobList();
      if (this.selectedJob) this.loadLogContent();
    },
    selectJob(name) {
      this.selectedJob = name;
      this.loadLogContent();
    },
    loadLogContent() {
      if (!this.selectedJob) return;
      this.loadingContent = true;
      this.$store.dispatch(FETCH_CRON_LOG_CONTENT, `${this.selectedJob}/?lines=${this.linesLimit}`)
        .then(data => {
          this.logLines = (data && data.lines) || [];
          this.logPath = (data && data.log_path) || "";
          this.loadingContent = false;
          this.$nextTick(() => {
            const el = this.$refs.logContent;
            if (el) el.scrollTop = el.scrollHeight;
          });
        })
        .catch(() => {
          this.logLines = [];
          this.loadingContent = false;
          this.$toastr.e("Failed to load log content");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: $blue;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f0f9ff; }
  &.sm { height: 28px; padding: 0 10px; font-size: 11px; }
}

.layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  min-height: 500px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.job-list {
  border-right: 1px solid #eef0f8;
  max-height: 640px;
  overflow-y: auto;
}

.job-row {
  padding: 12px 16px;
  border-bottom: 1px solid #f5f7fa;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: #f8fbff; }
  &.active { background: #eef7fb; border-left: 3px solid $blue; }
}

.job-name {
  font-size: 12.5px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  word-break: break-word;

  i { font-size: 10px; color: #a0aab8; }
}

.job-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.job-schedule {
  font-size: 10.5px;
  font-family: monospace;
  color: #7a8499;
  background: #f4f8fe;
  padding: 1px 6px;
  border-radius: 4px;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;

  &.s-yes { background: #dcfce7; color: $green; }
  &.s-no  { background: #e2e8f0; color: #718096; }
}

.job-modified {
  font-size: 10.5px;
  color: #a0aab8;
  margin-top: 4px;
}

.log-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.log-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f8;
  flex-wrap: wrap;
}

.log-path {
  font-size: 11px;
  font-family: monospace;
  color: #a0aab8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.log-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  select {
    height: 28px;
    border: 1.5px solid $border;
    border-radius: 6px;
    padding: 0 24px 0 8px;
    font-size: 11px;
    font-family: $font;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 8px center;
    appearance: none;
    cursor: pointer;

    &:focus { outline: none; border-color: $blue; }
  }
}

.log-content {
  flex: 1;
  margin: 0;
  padding: 14px 16px;
  background: #0d1b3e;
  color: #d6e4f0;
  font-size: 11.5px;
  font-family: 'SF Mono', Consolas, monospace;
  line-height: 1.6;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 640px;
}

.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
}
</style>
